/**
 * Fonctionnalités, without React: how a customer feature and its exceptions
 * read, which scopes can be picked, what a form must hold before it is sent,
 * and the requests themselves. The server checks all of it again; these only
 * decide what to offer and catch mistakes before a round trip.
 */
import type { StaffFeatureId, UserFeatureId } from "@wallyo/contracts/actions";
import {
  FEATURE_REGIONS,
  type CustomerFeatureListItem,
  type CustomerFeatureScope,
  type CustomerFeatureValue,
  type FeatureRegion,
  type FeatureTarget,
  type SetCustomerFeatureValueRequest,
} from "@wallyo/contracts/console/configuration";
import type { SettingCategory } from "@wallyo/contracts/settings";
import type { ConsoleClient } from "../../api/client";
import { removeCustomerFeatureValue, setCustomerFeatureValue } from "../../api/console-api";
import { t, type MessageId } from "../../i18n";
import { fromLocalInput, toLocalInput } from "../local-datetime";
import { isOwner } from "../team/team-access";
import { REASON_MIN_LENGTH } from "../team/team-writes";

export const FEATURE_STAFF_FEATURES = {
  manage: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
  search: "SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
  view: "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
} as const satisfies Record<string, StaffFeatureId>;

// ── How it reads ─────────────────────────────────────────────────────────

export type Audience = "clients" | "pros" | "everyone";

/** Who a feature is for, from the scopes it allows. */
export function audienceOf(feature: Pick<CustomerFeatureListItem, "scopes">): Audience {
  const clients = feature.scopes.includes("allClients") || feature.scopes.includes("user");
  const pros = feature.scopes.includes("allPros") || feature.scopes.includes("proProfile");
  if (clients && !pros) return "clients";
  if (pros && !clients) return "pros";
  return "everyone";
}

/** The features in the order the server lists them, one group per category. */
export function groupByCategory(
  features: readonly CustomerFeatureListItem[],
): Array<{ category: SettingCategory; features: CustomerFeatureListItem[] }> {
  const groups: Array<{ category: SettingCategory; features: CustomerFeatureListItem[] }> = [];
  for (const feature of features) {
    const group = groups.find((each) => each.category === feature.category);
    if (group) group.features.push(feature);
    else groups.push({ category: feature.category, features: [feature] });
  }
  return groups;
}

export const valueLabel = (value: boolean): string => t(value ? "features.on" : "features.off");

const SCOPE_LABELS: Record<CustomerFeatureScope, MessageId> = {
  system: "features.scope.system",
  region: "features.scope.region",
  appVersion: "features.scope.appVersion",
  allClients: "features.scope.allClients",
  allPros: "features.scope.allPros",
  user: "features.scope.user",
  proProfile: "features.scope.proProfile",
  clientProfile: "features.scope.clientProfile",
};

const REGION_LABELS: Record<FeatureRegion, MessageId> = {
  gn: "customers.region.gn",
  sn: "customers.region.sn",
  us: "customers.region.us",
  eu: "customers.region.eu",
};

export const scopeLabel = (scope: CustomerFeatureScope): string => t(SCOPE_LABELS[scope]);

export const regionLabel = (region: FeatureRegion): string => t(REGION_LABELS[region]);

/** Who a value reaches, in words. A person or pro profile shows its name. */
export function targetLabel(value: Pick<CustomerFeatureValue, "target" | "targetName">): string {
  const { target } = value;
  switch (target.scope) {
    case "system":
      return t("features.target.everyone");
    case "allClients":
      return t("features.target.allClients");
    case "allPros":
      return t("features.target.allPros");
    case "region":
      return regionLabel(target.region);
    case "appVersion":
      return t("features.target.appVersion", { version: target.minAppVersion });
    case "user":
      return value.targetName ?? t("features.target.unnamed");
    case "proProfile":
      return value.targetName ?? t("features.target.unnamed");
  }
}

/** The customer page a value's target links to: only a person has one (`user`). */
export const targetUserId = (target: FeatureTarget): string | null => (target.scope === "user" ? target.userId : null);

/** Why this person can't change the feature, or null when they can. The server decides again. */
export function featureLock(
  feature: Pick<CustomerFeatureListItem, "sensitivity">,
  features: readonly StaffFeatureId[],
): "critical" | null {
  return feature.sensitivity === "critical" && !isOwner(features) ? "critical" : null;
}

// ── Scopes ───────────────────────────────────────────────────────────────

export type ScopeChoice = {
  scope: CustomerFeatureScope;
  /** Why it can't be picked, or null when it can. */
  unavailable: "unsupported" | "needsSearch" | null;
};

/**
 * The scopes the picker offers: every scope the feature allows, in its
 * order. Only `settableScopes` can be picked; the others (a client profile,
 * today) are listed disabled with the reason. A person or pro profile is
 * found through the account search, so it needs SEARCH_ACCOUNTS.
 */
export function scopeChoices(feature: CustomerFeatureListItem, canSearch: boolean): ScopeChoice[] {
  return feature.scopes.map((scope) => ({
    scope,
    unavailable: !feature.settableScopes.includes(scope)
      ? "unsupported"
      : (scope === "user" || scope === "proProfile") && !canSearch
        ? "needsSearch"
        : null,
  }));
}

/** The scopes that can be picked. */
export const pickableScopes = (feature: CustomerFeatureListItem, canSearch: boolean): CustomerFeatureScope[] =>
  scopeChoices(feature, canSearch)
    .filter((choice) => choice.unavailable === null)
    .map((choice) => choice.scope);

// ── The form ─────────────────────────────────────────────────────────────

/** A person picked from the account search. A pro profile's id comes from their account page. */
export type PickedPerson = { userId: string; name: string | null; proProfileId: string | null };

export type ExceptionFields = {
  scope: CustomerFeatureScope | "";
  region: FeatureRegion | "";
  appVersion: string;
  person: PickedPerson | null;
  value: boolean;
  reason: string;
  /** A `datetime-local` value ("2026-10-01T18:00"), or "" for until changed. */
  expiresAt: string;
};

/** The server's shape for an app version (server/http/validation/console-configuration.ts). */
export const APP_VERSION_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,4}$/;

/** A new exception starts opposite the default: an exception that repeats the default does nothing. */
export function emptyFields(feature: Pick<CustomerFeatureListItem, "defaultValue">): ExceptionFields {
  return {
    scope: "",
    region: "",
    appVersion: "",
    person: null,
    value: !feature.defaultValue,
    reason: "",
    expiresAt: "",
  };
}

/** Editing an exception keeps its target; only the value, reason and expiry change. */
export function fieldsFrom(value: CustomerFeatureValue): ExceptionFields {
  const { target } = value;
  return {
    scope: target.scope,
    region: target.scope === "region" ? target.region : "",
    appVersion: target.scope === "appVersion" ? target.minAppVersion : "",
    person:
      target.scope === "user"
        ? { userId: target.userId, name: value.targetName, proProfileId: null }
        : target.scope === "proProfile"
          ? { userId: "", name: value.targetName, proProfileId: target.proProfileId }
          : null,
    value: value.value,
    reason: "",
    expiresAt: value.expiresAt ? toLocalInput(value.expiresAt) : "",
  };
}

/** The target the fields name, or null while it is incomplete. */
export function targetOf(fields: ExceptionFields): FeatureTarget | null {
  switch (fields.scope) {
    case "system":
    case "allClients":
    case "allPros":
      return { scope: fields.scope };
    case "region":
      return FEATURE_REGIONS.includes(fields.region as FeatureRegion)
        ? { scope: "region", region: fields.region as FeatureRegion }
        : null;
    case "appVersion":
      return APP_VERSION_PATTERN.test(fields.appVersion.trim())
        ? { scope: "appVersion", minAppVersion: fields.appVersion.trim() }
        : null;
    case "user":
      return fields.person?.userId ? { scope: "user", userId: fields.person.userId } : null;
    case "proProfile":
      return fields.person?.proProfileId ? { scope: "proProfile", proProfileId: fields.person.proProfileId } : null;
    default:
      return null;
  }
}

export const sameTarget = (a: FeatureTarget, b: FeatureTarget): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * What stops the form from being sent, in words, or null. `editing` is the
 * exception being changed (its target is fixed), null for a new one.
 */
export function exceptionProblem(
  feature: CustomerFeatureListItem,
  fields: ExceptionFields,
  context: {
    values: readonly CustomerFeatureValue[];
    editing: CustomerFeatureValue | null;
    canSearch: boolean;
    now: Date;
  },
): string | null {
  if (!context.editing && (fields.scope === "" || !pickableScopes(feature, context.canSearch).includes(fields.scope))) {
    return t("features.problem.scope");
  }
  const target = context.editing ? context.editing.target : targetOf(fields);
  if (!target) {
    switch (fields.scope) {
      case "region":
        return t("features.problem.region");
      case "appVersion":
        return t("features.problem.appVersion");
      case "proProfile":
        return t("features.problem.pro");
      default:
        return t("features.problem.person");
    }
  }
  if (!context.editing && context.values.some((each) => sameTarget(each.target, target))) {
    return t("features.problem.exists");
  }
  if (fields.reason.trim().length < REASON_MIN_LENGTH) return t("features.problem.reason");
  if (fields.expiresAt) {
    const at = fromLocalInput(fields.expiresAt);
    if (!at) return t("features.problem.expiryInvalid");
    if (at.getTime() <= context.now.getTime()) return t("features.problem.expiryPast");
  }
  return null;
}

/** The PUT body: the target, the value, the reason, the expiry, and the version when changing one. */
export function setRequest(
  fields: ExceptionFields,
  editing: CustomerFeatureValue | null,
): SetCustomerFeatureValueRequest {
  const target = editing ? editing.target : targetOf(fields);
  if (!target) throw new Error("setRequest needs a complete target: check exceptionProblem first");
  const expiresAt = fields.expiresAt ? fromLocalInput(fields.expiresAt) : null;
  return {
    target,
    value: fields.value,
    reason: fields.reason.trim(),
    expiresAt: expiresAt ? expiresAt.toISOString() : null,
    ...(editing ? { version: editing.version } : {}),
  };
}

export function sendSetException(
  client: ConsoleClient,
  settingId: UserFeatureId,
  request: SetCustomerFeatureValueRequest,
  idempotencyKey: string,
) {
  return setCustomerFeatureValue(client, settingId, request, { idempotencyKey });
}

export function sendRemoveException(
  client: ConsoleClient,
  value: CustomerFeatureValue,
  reason: string,
  idempotencyKey: string,
) {
  return removeCustomerFeatureValue(
    client,
    value.settingId,
    value.settingValueId,
    { reason: reason.trim(), version: value.version },
    { idempotencyKey },
  );
}

/** A problem with a removal's reason, or null. */
export const removeProblem = (reason: string): string | null =>
  reason.trim().length >= REASON_MIN_LENGTH ? null : t("features.problem.reason");
